import React, { useEffect, useState } from "react";
import { api, imgUrl, money } from "../../lib/api";
import { toast } from "sonner";
import { AlertTriangle, Plus, Minus } from "lucide-react";

export default function SellerInventory() {
  const [data, setData] = useState({ rows: [], low_stock: [] });
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("all");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [inv, lg] = await Promise.all([
        api.get("/seller/inventory"),
        api.get("/seller/inventory-logs?limit=50"),
      ]);
      setData(inv.data);
      setLogs(lg.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const adjust = async (productId, size, delta) => {
    try {
      await api.post(`/seller/products/${productId}/stock`, { size, delta, reason: delta > 0 ? "restock" : "manual_decrement" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setStock = async (productId, size) => {
    const v = window.prompt(`Set stock for size ${size}:`);
    if (v === null) return;
    const n = Number(v);
    if (!Number.isFinite(n) || n < 0) return toast.error("Enter a non-negative number");
    try {
      await api.post(`/seller/products/${productId}/stock`, { size, set_to: n, reason: "manual_set" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const rows = tab === "low" ? data.low_stock : data.rows;

  return (
    <div data-testid="seller-inventory-page">
      <div className="overline mb-2">Stock</div>
      <h1 className="font-display text-4xl mb-8">Inventory</h1>

      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={() => setTab("all")} className={`chip ${tab === "all" ? "bg-[hsl(var(--kb-primary))] text-white border-[hsl(var(--kb-primary))]" : ""}`} data-testid="tab-all">
          All ({data.rows.length})
        </button>
        <button onClick={() => setTab("low")} className={`chip ${tab === "low" ? "bg-[hsl(var(--kb-primary))] text-white border-[hsl(var(--kb-primary))]" : ""}`} data-testid="tab-low">
          <AlertTriangle size={12} className="inline mr-1" /> Low stock ({data.low_stock.length})
        </button>
      </div>

      <div className="bg-white border border-[hsl(var(--kb-border))] overflow-x-auto mb-8">
        {loading ? (
          <div className="p-8 text-center text-[hsl(var(--kb-muted))]" data-testid="inventory-loading">Loading inventory…</div>
        ) : error ? (
          <div className="p-8 text-center text-red-700" data-testid="inventory-error">{error}</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-[hsl(var(--kb-muted))]">{tab === "low" ? "No low-stock items." : "No products yet."}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--kb-surface-alt))] text-left text-[hsl(var(--kb-muted))]">
              <tr><th className="p-3">Product</th><th>Status</th><th>Sizes / Stock</th><th>Total</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.product_id} className="border-t border-[hsl(var(--kb-border))]" data-testid={`inv-row-${r.slug}`}>
                  <td className="p-3">
                    <div className="flex gap-3 items-center">
                      {r.image ? <img src={imgUrl(r.image)} alt="" className="w-10 aspect-[3/4] object-cover" /> : <div className="w-10 aspect-[3/4] bg-[hsl(var(--kb-surface-alt))]" />}
                      <div>
                        <div className="font-medium">{r.title}</div>
                        <div className="text-xs text-[hsl(var(--kb-muted))]">{money(r.price)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 uppercase text-xs">{r.status}</td>
                  <td className="p-3">
                    <div className="flex gap-2 flex-wrap">
                      {(r.sizes || []).length === 0 && <span className="text-xs text-[hsl(var(--kb-muted))]">No sizes</span>}
                      {(r.sizes || []).map((s) => (
                        <div key={s.size} className="inline-flex items-center gap-1 border border-[hsl(var(--kb-border))] px-2 py-1 text-xs">
                          <span className="font-medium">{s.size}</span>
                          <button onClick={() => adjust(r.product_id, s.size, -1)} className="text-red-700" aria-label="decrement"><Minus size={10} /></button>
                          <button onClick={() => setStock(r.product_id, s.size)} className="font-mono min-w-[1.5rem] text-center" data-testid={`set-stock-${r.slug}-${s.size}`}>
                            {s.stock}
                          </button>
                          <button onClick={() => adjust(r.product_id, s.size, +1)} className="text-green-700" aria-label="increment"><Plus size={10} /></button>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="p-3 font-medium">{r.total_stock}{r.total_stock <= 3 && <span className="ml-2 text-[10px] uppercase text-red-700">low</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white border border-[hsl(var(--kb-border))]">
        <div className="p-4 border-b border-[hsl(var(--kb-border))] overline">Recent Adjustments</div>
        {logs.length === 0 ? (
          <div className="p-6 text-center text-[hsl(var(--kb-muted))] text-sm">No stock adjustments yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[640px]">
              <thead className="bg-[hsl(var(--kb-surface-alt))] text-left text-[hsl(var(--kb-muted))]">
                <tr><th className="p-3">When</th><th>Product</th><th>Size</th><th>Before</th><th>After</th><th>Δ</th><th>Reason</th></tr>
              </thead>
              <tbody>
                {logs.map((l) => {
                  const before = l.previous_stock ?? l.before ?? 0;
                  const after = l.new_stock ?? l.after ?? 0;
                  const delta = l.change ?? l.delta ?? (after - before);
                  const sizeLabel = l.note?.startsWith("Size ") ? l.note.slice(5) : (l.size || "—");
                  return (
                    <tr key={l.id} className="border-t border-[hsl(var(--kb-border))]">
                      <td className="p-3 font-mono">{(l.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="p-3">{l.product?.title || l.product_id?.slice(0, 8)}</td>
                      <td className="p-3">{sizeLabel}</td>
                      <td className="p-3">{before}</td>
                      <td className="p-3">{after}</td>
                      <td className={`p-3 font-medium ${delta > 0 ? "text-green-700" : delta < 0 ? "text-red-700" : ""}`}>{delta > 0 ? `+${delta}` : delta}</td>
                      <td className="p-3 text-[hsl(var(--kb-muted))]">{l.reason}{l.reference_type && l.reference_type !== "manual" ? ` · ${l.reference_type}` : ""}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
